import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

/**
 * Modern MUI Pie Chart Component
 * Displays data in a pie chart format with theme-aware colors
 */
function ModernPieChart({ 
  data = [], 
  title, 
  subtitle,
  height = 300,
  colorMap = {},
  defaultColors = []
}) {
  const theme = useTheme();

  // Prepare data for recharts format
  const chartData = data.map((item) => ({
    name: item.label || item.name,
    value: item.value || 0
  }));

  // Get color for each data item
  const getColor = (name, index) => {
    const lowerName = name.toLowerCase();
    if (colorMap[lowerName]) return colorMap[lowerName];
    if (defaultColors[index]) return defaultColors[index];
    
    // Fallback to theme colors
    const themeColors = [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.secondary.main,
    ];
    return themeColors[index % themeColors.length];
  };

  const COLORS = chartData.map((item, index) => getColor(item.name, index));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Count: {payload[0].value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {(title || subtitle) && (
        <Box sx={{ mb: 2 }}>
          {title && (
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={Math.min(height * 0.3, 80)}
            innerRadius={Math.min(height * 0.18, 45)}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            align="center"
            layout="horizontal"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{
              fontSize: '12px',
              paddingTop: '15px'
            }}
            formatter={(value, entry, index) => {
              const total = chartData.reduce((sum, item) => sum + item.value, 0);
              const percentage = ((entry.payload.value / total) * 100).toFixed(1);
              return `${value} (${percentage}%)`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default ModernPieChart;
